import moment from 'moment-timezone';
import React, { useContext, useEffect, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Link, useOutletContext } from 'react-router-dom';
import styled from 'styled-components';
import DropdownSelect from '../../../components/DropdownSelect';
import HotelController, {
  AreaType,
  roomAssignmentNotificationListType,
} from '../../../controller/HotelController';
import { CurrentStoreContext } from '../../../controller/StoreController';
import { useToast } from '../../../controller/ToastController';
import { AuthContext } from '../../../navigation/AuthProvider';
import DeviceList from './HotelNoticeDeviceList';
import HotelNoticeReservationList from './HotelNoticeReservationList';

const alarmTimeList = [
  {
    key: 0,
    value: 30,
    name: '30초',
    label: '30초',
  },
  {
    key: 1,
    value: 60,
    name: '1분',
    label: '1분',
  },
  {
    key: 2,
    value: 120,
    name: '2분',
    label: '2분',
  },
  {
    key: 3,
    value: 180,
    name: '3분',
    label: '3분',
  },
  {
    key: 4,
    value: 60 * 60 * 24,
    name: '계속(확인할때까지)',
    label: '계속(확인할때까지)',
  },
];
const dateList = [
  {
    key: 0,
    value: moment().format('YYYY-MM-DD'),
    name: '오늘',
    label: '오늘',
  },
  {
    key: 1,
    value: moment().add(1, 'days').format('YYYY-MM-DD'),
    name: '내일',
    label: '내일',
  },
  {
    key: 2,
    value: moment().add(2, 'days').format('YYYY-MM-DD'),
    name: '모레',
    label: '모레',
  },
  {
    key: 3,
    value: moment().add(3, 'days').format('YYYY-MM-DD'),
    name: '글피',
    label: '글피',
  },
];
const hourList = Array.from({ length: 48 }, (_, i) => {
  const hour = Math.floor(i / 2);
  const minute = i % 2 === 0 ? '00' : '30';
  return {
    key: i,
    value: `${hour.toString().padStart(2, '0')}:${minute}:00`,
    name: `${hour}시 ${minute}분`,
    label: `${hour}시 ${minute}분`,
  };
});
const minuteList = Array.from({ length: 24 }, (_, i) => {
  const value = (i + 1) * 5;
  return {
    key: i,
    value: value,
    name: `${value.toLocaleString()}분`,
    label: `${value.toLocaleString()}분`,
  };
});
const audioFiles = [
  {
    id: 'audio1',
    label: '자연 소리',
    soundType: 0,
    src: `${process.env.PUBLIC_URL}/audio/checkout01.webm`,
  },
  {
    id: 'audio2',
    label: '전화벨',
    soundType: 1,
    src: `${process.env.PUBLIC_URL}/audio/checkout02.webm`,
  },
  {
    id: 'audio3',
    label: '부드러운 음악',
    soundType: 2,
    src: `${process.env.PUBLIC_URL}/audio/checkout03.webm`,
  },
  {
    id: 'audio4',
    label: '편안한  음악',
    soundType: 3,
    src: `${process.env.PUBLIC_URL}/audio/checkout04.webm`,
  },
  {
    id: 'audio5',
    label: '알림음',
    soundType: 4,
    src: `${process.env.PUBLIC_URL}/audio/checkout05.mp3`,
  },
];

const HotelCheckOut = (oProps: any) => {
  const showToast = useToast();
  const [hourSelectDefalut, setHourSelectDefalut] = useState<any>({
    key: -1,
    value: undefined,
    name: `시간을 선택해주세요`,
    label: `시간을 선택해주세요`,
  });
  const saveMomo: string[] = oProps.saveMomo;
  const checkoutType: number = oProps.checkoutType;
  const { currentStore } = useOutletContext<CurrentStoreContext>();
  const oAuthContext = useContext(AuthContext);
  const user_id = oAuthContext.userInfo.user_id;
  const [deviceList, setDeviceList] = useState<AreaType[]>();
  const [areaList, setAreaList] = useState<any[]>([]);
  const [refresh, setRefresh] = useState<boolean>(false);
  const [dateCheckbox, setDateCheckbox] = useState(false);
  const [date, setDate] = useState<any>(dateList[0]);
  const [hour, setHour] = useState<any>(hourSelectDefalut);
  const [minute, setMinute] = useState<any>(hourSelectDefalut);
  const [alarmTime, setAlarmTime] = useState<any>(alarmTimeList[0]);
  const [isPlaying, setIsPlaying] = useState<string | null>(null);
  const [audioType, setAudioType] = useState<number | null>(null);
  const [checkoutText, setCheckoutText] = useState('');
  const [period, setPeriod] = useState(new Date());
  const timeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const [notificationList, setNotificationList] = useState<roomAssignmentNotificationListType[]>();

  // 알림설정
  const audioRefs: any = {
    audio1: useRef<HTMLAudioElement>(null),
    audio2: useRef<HTMLAudioElement>(null),
    audio3: useRef<HTMLAudioElement>(null),
    audio4: useRef<HTMLAudioElement>(null),
    audio5: useRef<HTMLAudioElement>(null),
  };
  const playAudio = (key: string, soundType: number) => {
    if (timeoutIdRef.current) {
      clearTimeout(timeoutIdRef.current);
    }
    Object.keys(audioRefs).forEach((refKey) => {
      if (audioRefs[refKey].current && refKey !== key) {
        audioRefs[refKey].current.pause();
      }
    });

    if (audioRefs[key].current) {
      audioRefs[key].current.play();
      setIsPlaying(key);
      setAudioType(soundType);

      // 8초 후에 자동으로 멈추기
      timeoutIdRef.current = setTimeout(() => {
        handleEnded(key);
      }, 8000);
    }
  };
  const handleEnded = (key: string) => {
    if (audioRefs[key].current) {
      audioRefs[key].current.pause();
      setIsPlaying(null);
    }
  };
  const textAreaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCheckoutText(event.target.value);
  };
  const handleDateCheckbox = (e: any) => {
    setDateCheckbox(e.target.checked);
  };
  const onChangeDate = (e: any) => {
    setDate(e);
  };
  const handleDateChange = (date: any) => {
    setPeriod(date);
  };
  const onChangeHour = (e: any) => {
    setHour(e);
  };
  const onChangeMinute = (e: any) => {
    setMinute(e);
  };
  const onChangeAlarmTime = (e: any) => {
    setAlarmTime(e);
  };
  const resetForm = () => {
    setAreaList([]);
    setCheckoutText('');
    setAudioType(null);
    setIsPlaying(null);
    setAlarmTime(alarmTimeList[0]);
    setDate(dateList[0]);
    setHour(hourSelectDefalut);
    setMinute(hourSelectDefalut);
    setDateCheckbox(false);
    setPeriod(new Date());
  };
  const getDeviceList = async () => {
    try {
      const oParams = {
        store_id: currentStore.value,
        user_id: user_id,
      };
      const oRes: any = await HotelController.getDevice(oParams);

      if (oRes && oRes.data) {
        const oResponse: any = oRes.data.data;
        setDeviceList(oResponse);
      }
    } catch (err) {
      console.log('err-->', err);
    }
  };
  const setAlert = async () => {
    try {
      if (areaList.length === 0) {
        showToast('객실을 선택해주세요.', 'error');
      } else if (checkoutType !== 2 && hour?.value === undefined) {
        showToast('퇴실시간을 선택해주세요.', 'error');
      } else if (checkoutType !== 2 && minute?.value === undefined) {
        showToast('알람시간을 선택해주세요.', 'error');
      } else if (audioType === null) {
        showToast('알림음을 선택해주세요.', 'error');
      } else {
        const oParams = {
          store_id: currentStore.value,
          can_extend_purchase_by_area_id: areaList,
          notice_type: checkoutType,
          content: checkoutText,
          sound_type: audioType,
          sound_duration_sec: alarmTime.value,
          checkout_date: dateCheckbox ? moment(period).format('YYYY-MM-DD') : date.value,
          checkout_time: hour.value,
          notice_before_min: minute?.value,
        };
        const oRes: any = await HotelController.setAlert(oParams);

        if (oRes && oRes.data.ok) {
          resetForm();
          showToast('알림을 발송했습니다.');
          setRefresh(true);
          if (audioType !== null) handleEnded(`audio${audioType + 1}`);
          {
            checkoutType !== 2 && getRoomNotificationList();
          }
        } else {
          showToast('다시 시도해주세요.', 'error');
        }
      }
    } catch (err) {
      console.log('err-->', err);
    }
  };
  // 예약 리스트
  const getRoomNotificationList = async () => {
    const fetchFunction =
      checkoutType === 0
        ? HotelController.getRegularNotificationList
        : HotelController.getRoomAssignmentNotificationList;

    try {
      const oParams = {
        store_id: currentStore.value,
      };

      const oRes: any = await fetchFunction(oParams);
      if (!oRes?.data?.data) return;

      let notificationData;
      if (checkoutType === 0) {
        notificationData = oRes.data.data.regularNotificationList;
      } else {
        notificationData = oRes.data.data.roomAssignmentNotificationList;
      }
      setNotificationList(notificationData);
    } catch (err) {
      console.log('err-->', err);
    }
  };

  useEffect(() => {
    getDeviceList();
    setRefresh(false);
    oProps.getLatestCheckoutSentence();
  }, [refresh]);
  useEffect(() => {
    setCheckoutText('');
    if (checkoutType !== 2) {
      getRoomNotificationList();
    }
  }, [checkoutType]);

  return (
    <Style>
      <div className="contentContainer">
        <Link to={'/room/checkList'} className="checkListButton">
          퇴실 알림 결과 보기
        </Link>
        {checkoutType !== 2 && (
          <div className="flex noticeText">
            <img src={`${process.env.PUBLIC_URL}/svg/ic_warning.svg`} alt="퇴실 아이콘" />
            <p>호실 지정 알림이 설정된 객실은 당일 일괄 알림이 전송되지 않습니다.</p>
          </div>
        )}
        <div className="HotelNoticeReservationListWrap">
          {checkoutType !== 2 && (
            <HotelNoticeReservationList
              notificationList={notificationList}
              getRoomNotificationList={getRoomNotificationList}
              checkoutType={checkoutType}
            />
          )}
        </div>
      </div>
      <div className="contentContainer">
        <div className="title">
          <h4>객실선택</h4>
        </div>
        <div className="deviceListContainer">
          <DeviceList
            deviceList={deviceList}
            setAreaList={setAreaList}
            refresh={refresh}
            checkoutType={checkoutType}
          />
        </div>
      </div>
      <div className="contentContainer">
        <div className="content">
          <div className="title">
            <h4>퇴실알림 안내 문구</h4>
          </div>
          <textarea
            name="checkoutText"
            id="checkoutText"
            value={checkoutText}
            onChange={textAreaChange}
            placeholder="퇴실알림 안내 내용을 입력해주세요"
          />

          {saveMomo?.length > 0 && (
            <div className="saveMemoWrap">
              <p>최근 발송된 문구</p>
              <div className="memoList">
                {saveMomo.map((data, index: number) => (
                  <button key={index} onClick={() => setCheckoutText(data)}>
                    {data}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
        {checkoutType !== 2 && (
          <div className="content">
            <div className="title">
              <h4>퇴실알림 예약 시간</h4>
            </div>
            {checkoutType === 1 && (
              <div className="alarmWrap flex">
                <img src={`${process.env.PUBLIC_URL}/svg/ic_calendar_black.svg`} alt="달력아이콘" />
                <p>퇴실 날짜</p>
                <DropdownSelect
                  options={dateList}
                  defaultValue={date}
                  onChangeValue={(e: any) => onChangeDate(e)}
                />
                <div className="flex">
                  <label className="checkboxWrap" htmlFor="checkbox">
                    <input
                      className="checkbox"
                      id="checkbox"
                      type="checkbox"
                      checked={dateCheckbox}
                      onChange={handleDateCheckbox}
                    />
                    <span>직접설정</span>
                  </label>
                  <div className="calendarWrap">
                    <DatePicker
                      className="datePicker"
                      selected={period}
                      onChange={(date) => handleDateChange(date)}
                      dateFormat="yy/MM/dd"
                      isClearable={false}
                      locale="ko"
                      minDate={new Date()}
                      disabledKeyboardNavigation={true}
                      onFocus={(e) => {
                        e.preventDefault();
                        e.target.blur();
                      }}
                    />
                    <div className="calendar">
                      <img src={`${process.env.PUBLIC_URL}/svg/ic_calendar.svg`} alt="달력아이콘" />
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="alarmWrap flex">
              <img src={`${process.env.PUBLIC_URL}/svg/ic_door.svg`} alt="퇴실 아이콘" />
              <p>퇴실 시간</p>
              <DropdownSelect
                options={hourList}
                defaultValue={hour}
                onChangeValue={(e: any) => onChangeHour(e)}
              />
            </div>
            <div className="alarmWrap flex">
              <img src={`${process.env.PUBLIC_URL}/svg/ic_bell.svg`} alt="알림 아이콘" />
              <p>알람 시간</p>
              <DropdownSelect
                options={minuteList}
                defaultValue={minute}
                onChangeValue={(e: any) => onChangeMinute(e)}
              />
              <p>분 전에 알림이 울립니다.</p>
            </div>
          </div>
        )}
        <div className="content">
          <div className="title">
            <h4>알림음 선택</h4>
          </div>
          {audioFiles.length > 0 && (
            <div className="audioList flex">
              {audioFiles.map((audio, idx) => (
                <div key={audio.id}>
                  <audio
                    ref={audioRefs[audio.id]}
                    // onEnded={() => handleEnded(audio.id)}
                    src={audio.src}
                  />
                  <button onClick={() => playAudio(audio.id, audio.soundType)}>
                    <div
                      className={` ${
                        audioType === idx ? 'alarmBox flex active' : 'alarmBox flex'
                      }`}>
                      <p>{audio.label}</p>
                      <img
                        src={
                          audioType === idx
                            ? `${process.env.PUBLIC_URL}/svg/ic_alarm_active.svg`
                            : `${process.env.PUBLIC_URL}/svg/ic_alarm.svg`
                        }
                        alt="알림아이콘"
                      />
                    </div>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="content">
          <div className="title">
            <h4>알림음 재생 시간</h4>
          </div>
          <div className="selectorWrap flex">
            <DropdownSelect
              options={alarmTimeList}
              defaultValue={alarmTime}
              onChangeValue={(e: any) => onChangeAlarmTime(e)}
            />
            <p>동안 알람이 재생됩니다.</p>
          </div>
        </div>

        <button onClick={setAlert} className="submitButton">
          {checkoutType === 0 && <p>저장하기</p>}
          {checkoutType === 1 && <p>알림 예약하기</p>}
          {checkoutType === 2 && <p>즉시 알림 전송하기</p>}
        </button>
      </div>
    </Style>
  );
};

export default HotelCheckOut;

const Style = styled.div`
  /* 공통 */
  p {
    font-size: 14px;
    font-weight: 500;
    color: #979797;
  }

  .contentContainer:nth-child(1) {
    margin-top: 10px;
    border-radius: 10px 10px 0 0;
  }
  .contentContainer:nth-child(2) {
    border-radius: 0;
  }
  .contentContainer:nth-child(3) {
    border-radius: 0 0 10px 10px;
  }
  .flex {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .checkListButton {
    display: inline-block;
    padding: 10px 28px 8px 28px;
    border-radius: 4px;
    color: #fff;
    background: #4dd2ae;
  }
  .noticeText {
    margin-top: 6px;
  }
  .HotelNoticeReservationListWrap {
    margin-top: 11px;
  }
  .deviceListContainer {
    max-height: 550px;
    overflow-y: scroll;
  }

  .content {
    margin-bottom: 55px;
  }
  #checkoutText {
    width: 100%;
    height: 150px;
    max-height: 150px;
    padding: 24px;
    border: 1px solid #e5e5e5;
    border-radius: 5px;
  }
  .audioList {
    flex-wrap: wrap;

    button {
      padding: 0;
    }
  }
  .saveMemoWrap {
    margin-top: 6px;
  }
  .memoList {
    display: flex;
    align-items: center;
    gap: 2%;

    button {
      min-width: 22%;
      padding: 10px;
      margin-top: 4px;
      border: 2px solid #979797;
      border-radius: 4px;
      text-align: left;
      color: #777;
      font-weight: 400;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
    }
    button:hover {
      background: #333;
      font-weight: 500;
      border-color: #333;
      color: #fff;
    }
  }
  .alarmBox {
    border: 1px solid #cecece;
    border-radius: 5px;
    padding: 0;
    cursor: pointer;

    img {
      padding: 9px;
      border-left: 1px solid #cecece;
    }
    p {
      padding-left: 18px;
      font-size: 14px;
      line-height: 19px;
    }
  }
  .alarmBox.active {
    border: 1px solid #4dd2ae;

    img {
      padding: 9px;
      border-color: #4dd2ae;
    }
    p {
      color: #4dd2ae;
    }
  }
  .alarmWrap {
    flex-wrap: wrap;
    margin-top: 15px;
  }
  .selectorWrap {
    p {
      color: #555;
    }
  }
  .selector,
  .datePicker {
    min-width: 170px;
    max-width: 170px;
  }
  .backButton {
    margin-bottom: 15px;
  }

  .checkboxWrap {
    display: flex;
    gap: 6px;
    cursor: pointer;

    span {
      font-size: 14px;
      color: #979797;
    }
    .checkbox {
      accent-color: #40977f;
    }
  }
  .calendarWrap {
    position: relative;
    width: 100%;

    .calendar {
      position: absolute;
      right: 20px;
      top: 9px;
    }
  }
  .datePicker {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 9px 10px;
    padding-right: 30px;
    border-radius: 5px;
    border: 1px solid #cecece;
    color: #a7a7a7;
    font-weight: 400;
    font-size: 16px;
    cursor: pointer;
    letter-spacing: 0.5px;
    input {
      display: none;
    }
  }
  .submitButton p {
    padding: 6px 0;
    font-size: 20px;
    font-weight: 700;
    color: #fff;
  }
  .deviceList {
    gap: 1%;
    .roomButton {
      max-width: 110px;
    }
  }
`;
